import React, { useEffect, useReducer } from "react"
import { useSelector, useDispatch } from "react-redux"

import Login from "./SignIn/Login"
import SignInOtp from "./SignIn/Otp"
import SignUp from "./SignIn/SignUp"
import NeedHelp from "./NeedHelp/NeedHelp"
import ConflictEmailList from "./ConflictCases/ConflictEmailList"
import EmailConflict from "./ConflictCases/EmailConflict"
import PhoneConflict from "./ConflictCases/PhoneConflict"
import TokenMerge from "./ConflictCases/TokenMerge"
import OtpConflict from "./ConflictCases/OtpConflict"
import css from "./Auth.scss"
import {
    updateAuthHistory,
    setDefault,
    setAuthType,
    updateAuthenticationInfo,
    setConflictCaseInfo,
    setModalName,
    defaultState,
    newAuthReducer,
} from "./reducer"

import {
    setLoggedIn,
    updateUserData,
    setDefaultAuthConfig,
    updateAuthConfig,
} from "@containers/App/reducer.js"
import Dialog from "@components/Dialog/Dialog.js"
import SuccessScreen from "./SignIn/SuccessScreen"
import Slider from "./InternalComponents/Slider"

const Auth = () => {
    const { authConfig, isLogin } = useSelector((state) => state.shellReducer)
    const [state, localDispatch] = useReducer(newAuthReducer, defaultState)
    const { authType, authHistory, info, modalName } = state
    const dispatch = useDispatch()
    useEffect(() => {
        if (authConfig?.populateData?.conflict) {
            localDispatch(
                setConflictCaseInfo({
                    requiredDetail: authConfig.populateData.requiredDetail,
                    conflict: authConfig.populateData.conflict,
                    description: authConfig.populateData?.description,
                    header: authConfig.populateData?.header,
                    cta: authConfig.populateData?.cta,
                    optionsList: authConfig.populateData?.optionsList?.map((val) => {
                        return {
                            value: val.email,
                            display: val.email,
                        }
                    }),
                    number: authConfig.populateData?.number,
                })
            )
            authConfig.populateData.requiredDetail === "email"
                ? localDispatch(setAuthType("ConflictEmailList"))
                : localDispatch(setAuthType("PhoneConflict"))
            dispatch(updateAuthConfig({ showAuth: true }))
        } else if (authConfig?.populateData?.phone) {
            localDispatch(updateAuthenticationInfo({ phone: authConfig.populateData.phone }))
            localDispatch(setAuthType("MerchantOtp"))
            dispatch(updateAuthConfig({ showAuth: true }))
        }
    }, [])
    useEffect(() => {
        if (!isLogin) {
            localDispatch(setAuthType("SignUp"))
        } else {
            localDispatch(setAuthType("Login"))
        }
    }, [isLogin])
    /**
     * @description The function below renders the component based on the current authType
     */
    const renderAuth = () => {
        switch (authType) {
            case "SignInOtp":
                return (
                    <SignInOtp
                        backHandler={backHandler}
                        onSuccess={onSuccess}
                        forwardHandler={forwardHandler}
                        state={state}
                        localDispatch={localDispatch}
                    />
                )

            case "MerchantOtp":
                return (
                    <SignInOtp
                        backHandler={backHandler}
                        onSuccess={onSuccess}
                        forwardHandler={forwardHandler}
                        state={state}
                        localDispatch={localDispatch}
                    />
                )
            case "SignUp":
                return (
                    <SignUp
                        forwardHandler={forwardHandler}
                        backHandler={backHandler}
                        state={state}
                        localDispatch={localDispatch}
                    />
                )
            case "NeedHelp":
                return (
                    <NeedHelp forwardHandler={forwardHandler} state={state} localDispatch={localDispatch} />
                )
            case "ConflictEmailList":
                return (
                    <ConflictEmailList
                        forwardHandler={forwardHandler}
                        state={state}
                        localDispatch={localDispatch}
                    />
                )
            case "EmailConflict":
                return (
                    <EmailConflict
                        forwardHandler={forwardHandler}
                        onSuccess={onSuccess}
                        state={state}
                        localDispatch={localDispatch}
                    />
                )
            case "PhoneConflict":
                return (
                    <PhoneConflict
                        forwardHandler={forwardHandler}
                        onSuccess={onSuccess}
                        state={state}
                        localDispatch={localDispatch}
                    />
                )
            case "TokenMerge":
                return (
                    <TokenMerge
                        backHandler={backHandler}
                        onSuccess={onSuccess}
                        forwardHandler={forwardHandler}
                        state={state}
                        localDispatch={localDispatch}
                    />
                )

            case "OtpConflict":
                return (
                    <OtpConflict
                        backHandler={backHandler}
                        onSuccess={onSuccess}
                        forwardHandler={forwardHandler}
                        state={state}
                        localDispatch={localDispatch}
                    />
                )
            default:
                return (
                    <Login
                        forwardHandler={forwardHandler}
                        onSuccess={onSuccess}
                        state={state}
                        localDispatch={localDispatch}
                    />
                )
        }
    }
    /**
     *
     * @description the function below renders the popup after user has done login/signup successfully or has submitted query (in need help widget)successfully
     */
    const renderPopup = () => {
        switch (modalName) {
            case "NeedHelp":
                return (
                    <SuccessScreen
                        closePopup={true}
                        title={"Thank You for reaching us"}
                        message={"We'll get back to you shortly"}
                        onCloseAction={() => localDispatch(setModalName(null))}
                    />
                )
            default:
                return (
                    <SuccessScreen
                        closePopup={false}
                        title={info.existingUser === true ? "Login Successful" : "Sign up Successful"}
                    />
                )
        }
    }
    /**
     * @description The primary job of this function is to maintain the entire history during the authentication journey.
     *              It set's the current page type as well as push the previous page type into the stack
     * @param {String} value Expects a single string based arguments
     */
    const forwardHandler = (value) => {
        const initialState = [...authHistory]

        if (initialState.length === 0 || initialState[initialState.length - 1] !== value) {
            initialState.push(authType)
            localDispatch(setAuthType(value))
            localDispatch(updateAuthHistory(initialState))
        }
    }

    /**
     * @description This function works exactly the opposite of the above function, but also updates the history
     */

    const backHandler = () => {
        const initialState = [...authHistory]
        localDispatch(setAuthType(initialState.pop()))
        localDispatch(updateAuthHistory(initialState))
    }

    /**
     * @description If a redirect url is passed then the user is redirected to that particular page, otherwise a success message is displayed
     * @param {String} value Expects a single string based arguments
     */

    const onSuccess = (userData, redirect_url = null) => {
        dispatch(setLoggedIn(true))
        dispatch(
            updateUserData({
                emailId: userData.email,
                PHONE_NUMBER: userData.number,
                username: userData.name,
                userId: userData.username,
                hashedEmail: "",
            })
        )
        dispatch(setDefaultAuthConfig())
        localDispatch(setModalName("SignInSuccess"))
        setTimeout(() => {
            if (redirect_url) {
                window.location.href = redirect_url
            }
            if (authConfig?.onSuccess) {
                authConfig.onSuccess()
                localDispatch(setDefault())
            } else {
                window.location.reload()
            }
        }, 1500)
    }

    return (
        <>
            <Dialog
                isOpen={authConfig.showAuth === true || modalName}
                onOverlayClick={() => {
                    if (!authConfig.restrictUserFromClosingAuth) {
                        dispatch(setDefaultAuthConfig())
                        localDispatch(setDefault())
                    }
                }}
            >
                {authConfig.showAuth === true && (
                    <div className={`${css.container} flex`}>
                        <Slider />
                        {renderAuth()}
                    </div>
                )}
                {modalName && <>{renderPopup()}</>}
            </Dialog>
        </>
    )
}

export default Auth
